import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges } from "@angular/core";

@Component({
  selector: "app-dynamic-table",
  templateUrl: "./dynamic-table.component.html",
  styleUrls: ["./dynamic-table.component.scss"],
})
export class DynamicTableComponent implements OnInit {
  @Input() type?: string;// table heading
  @Input() columns: string[] = []; // array of column names
  @Input() rows: any[] = []; // array of objects with various possible structures
  @Input() actionColumn?: boolean = false; // whether to show action column or not
  @Input() actionIcon?: string = "fa-trash"; // icon class for action button
  @Input() enableUnitDropdown?: boolean = false; // whether to enable unit dropdown functionality
  @Input() unitColumnName?: string = "Unit"; // name of the column that should have unit dropdown
  @Output() actionClicked = new EventEmitter<number>(); // emit index of row clicked for action
  @Output() unitChanged = new EventEmitter<{
    rowIndex: number;
    unitData: any;
  }>(); // emit when unit selection changes

  // Track selected unit for each row
  selectedUnits: { [rowIndex: number]: string } = {};

  heading = "";

  ngOnInit() {
    this.heading = this.type? `${this.type} Data`: '';
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['rows'] && this.rows?.length) {
      this.initializeUnitSelection();
    }
  }

  private initializeUnitSelection() {
    
    this.rows.forEach((row, index) => {
      // Check if row has pricePerUnit array
      if (
        row.pricePerUnit &&
        Array.isArray(row.pricePerUnit) &&
        row.pricePerUnit.length
      ) {
        this.selectedUnits[index] = row.pricePerUnit[0].unit;
      }
      // For array rows, we don't need unit selection
    });
  }

  onActionClick(index: number) {
    this.actionClicked.emit(index);
  }

  onUnitChange(rowIndex: number, unitValue: string) {
    
    this.selectedUnits[rowIndex] = unitValue;

    // Find the unit data that was selected
    const row = this.rows[rowIndex];
    
    if (row.pricePerUnit) {
      const unitData = row.pricePerUnit.find(
        (pu) => pu.unit === unitValue
      );
      // Emit the selected unit data
      this.unitChanged.emit({ rowIndex, unitData });
    }
  }

  // Get the display value based on column name and current row
  getValue(row: any, column: string, rowIndex: number): any {
    // Handle array rows (simple string arrays)
    if (Array.isArray(row)) {
      const colIndex = this.columns.findIndex(
        (col) =>
          this.normalizeColumnName(col) === this.normalizeColumnName(column)
      );
      return colIndex >= 0 && colIndex < row.length ? row[colIndex] : "";
    }

    // Handle rows with pricePerUnit structure
    if (row.pricePerUnit) {
      return this.getValueForUnitRow(row, column, rowIndex);
    }

    // Handle simple object rows
    return this.getValueForSimpleRow(row, column);
  }

  private getValueForUnitRow(row: any, column: string, rowIndex: number): any {
    const columnLower = this.normalizeColumnName(column);

    if (columnLower === "item" || columnLower === "name") {
      return row.name;
    }

    // Get the currently selected unit for this row
    const selectedUnit = this.selectedUnits[rowIndex];

    // If no unit is selected yet, return empty
    if (!selectedUnit) return "";

    // Find the unit data that matches the selected unit
    const unitData = row.pricePerUnit.find(
      (pu: any) => pu.unit === selectedUnit
    );

    if (!unitData) return "";

    if (columnLower === this.normalizeColumnName(this.unitColumnName)) {
      return selectedUnit;
    } else if (columnLower === "costprice") {
      return unitData.costPrice?.value || "";
    } else if (columnLower === "sellingprice") {
      return unitData.sellingPrice?.value || "";
    }

    return "";
  }

  private getValueForSimpleRow(row: any, column: string): any {
    // First check if there's a direct match with the column name
    const propName = this.normalizeColumnName(column);
    if (row[propName] !== undefined) {
      return row[propName];
    }

    // Handle nested properties with dot notation (e.g., 'user.name')
    if (column.includes(".")) {
      return this.getNestedProperty(row, column);
    }

    // Try case-insensitive match
    const lowerProp = column.toLowerCase();
    for (const key in row) {
      if (key.toLowerCase() === lowerProp) {
        return row[key];
      }
    }

    // Default case
    return "";
  }

  // Get nested property using dot notation (e.g., 'user.name')
  private getNestedProperty(obj: any, path: string): any {
    const parts = path.split(".");
    let current = obj;

    for (const part of parts) {
      if (current === null || current === undefined) {
        return "";
      }
      current = current[this.normalizeColumnName(part)];
    }

    return current !== undefined ? current : "";
  }

  // Get the available units for a row
  getAvailableUnits(row: any): string[] {
    if (!row.pricePerUnit || Array.isArray(row)) return [];
    return row.pricePerUnit.map((pu: any) => pu.unit);
  }

  // Check if column should show dropdown
  isUnitDropdownColumn(column: string): boolean {
    return (
      this.normalizeColumnName(column) ===
        this.normalizeColumnName(this.unitColumnName)
    );
  }

  // Helper method to normalize column/property names
  private normalizeColumnName(column: string): string {
    return column.toLowerCase().replace(/\s+/g, "");
  }

  // Check if row has unit data structure
  isUnitDataRow(row: any): boolean {
    return !Array.isArray(row) && row.pricePerUnit !== undefined;
  }
}
